import { exportToCSV } from '../../../../utils/exportUtils';
import React, { useState, useRef } from 'react';
import AppCard from '../../../../components/ui/AppCard';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import apiClient from "../../../../services/api-client";

import "primereact/resources/themes/saga-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { useMountEffect } from 'primereact/hooks';
import StorageHelper from '../../../../utils/storage-helper';
import ChangeStatusCooperateReceiptDialog from '../../components/cooperate_receipts/ChangeStatusCooperateReceiptDialog';

const AllCooperateReceiptsTable = () => {
    const [receipts, setReceipts] = useState([]);
    const [globalFilter, setGlobalFilter] = useState('');
    const [loading, setLoading] = useState(false);
    const [showStatusDialog, setShowStatusDialog] = useState(false);
    const [selectedReceipt, setSelectedReceipt] = useState(null);

    const dt = useRef(null);

    useMountEffect(() => {
        fetchReceipts();
    }, []);

    const fetchReceipts = async () => {
        setLoading(true);
        try {
            const response = await apiClient.get('cooperateReceipt/all', {
                headers: {
                    Authorization: 'Bearer ' + StorageHelper.get('token')
                }
            });

            if (response.data.status === "SUCCESS") {
                setReceipts(response.data.responseBody);
            } else {
                console.error('Failed to fetch receipts:', response.data.status);
            }
        } catch (error) {
            console.error('Error fetching receipts:', error);
        } finally {
            setLoading(false);
        }
    };


    const openStatusDialog = (receipt) => {
        setSelectedReceipt(receipt);
        setShowStatusDialog(true);
    };

    const closeStatusDialog = () => {
        setShowStatusDialog(false);
        setSelectedReceipt(null);
    };

    const handleStatusUpdate = (receiptNumber, newStatus) => {
        setReceipts(prevReceipts =>
            prevReceipts.map(receipt =>
                receipt.receiptNumber === receiptNumber
                    ? {...receipt, status: newStatus}
                    : receipt
            )
        );
    };

    const onGlobalFilter = (event) => {
        setGlobalFilter(event.target.value);
    };

    const amountBodyTemplate = (rowData) => {
        return `${rowData.currency || ''} ${parseFloat(rowData.amount || 0).toFixed(2)}`;
    };

    const dateBodyTemplate = (rowData, field) => {
        const date = rowData[field];
        if (!date) return '-';
        return new Date(date).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
    };

    const statusBodyTemplate = (rowData) => {
        const getStatusStyle = (status) => {
            switch (status) {
                case 'PENDING':
                    return {color: '#FCB700', backgroundColor: '#FFF8E1'};
                case 'RECEIPTED':
                    return {color: '#00BAFE', backgroundColor: '#E6F7FF'};
                case 'COMPLETED':
                    return {color: '#00D390', backgroundColor: '#E6FFF7'};
                case 'REVOKE REQUESTED':
                    return {color: '#6C5CE7', backgroundColor: '#F3EFFF'};
                case 'FAILED':
                    return {color: '#FF6266', backgroundColor: '#FFECEC'};
                case 'REVOKED':
                    return {color: '#FF6266', backgroundColor: '#FFECEC'};
                default:
                    return {color: '#A0A0A0', backgroundColor: '#F5F5F5'};
            }
        };

        const style = getStatusStyle(rowData.status);
      

  return (
            <span
                style={{
                    backgroundColor: style.backgroundColor,
                    color: style.color,
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    fontWeight: 'bold',
                    fontSize: '0.85rem'
                }}
            >
                {rowData.status}
            </span>
        );
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="actions-container flex items-center justify-center gap-2">
                <div className="tooltip" data-tip="Change Status">
                    <button
                        className="btn btn-sm btn-circle btn-ghost text-base-content/70 hover:text-base-content hover:bg-base-200"
                        onClick={() => openStatusDialog(rowData)}
                    >
                        <i className="pi pi-pencil text-lg"></i>
                    </button>
                </div>
            </div>
        );
    };

        const paymentModeBodyTemplate = (rowData) => {
        const mode = rowData.modeOfPayment;
        if (!mode) return null;
        return <span className="badge badge-outline text-sm">{mode.toString().toUpperCase().replaceAll("CR_", "")}</span>;
    };

    return (

        <div className="w-full">
                <AppCard title="All Corporate Receipts Report">
                    <div
                        className="flex flex-col sm:flex-row gap-4 justify-between items-center w-full mb-6 bg-base-100/50 p-4 rounded-xl border border-base-200">
                        <div className="relative w-full max-w-xs">
                            <i className="pi pi-search absolute left-3 top-1/2 -translate-y-1/2 text-base-content/50"></i>
                            <input
                                type="text"
                                className="input input-bordered w-full pl-10 h-10 shadow-sm bg-base-100 rounded-full"
                                placeholder="Search receipts..."
                                value={globalFilter}
                                onChange={onGlobalFilter}
                            />
                        </div>
                        <div className="flex items-center gap-3 ml-auto">
                            <div className="dropdown dropdown-end">
                                <div tabIndex={0} role="button"
                                     className="btn btn-outline btn-ghost h-10 min-h-0 shadow-sm rounded-full">
                                    <i className="pi pi-file-export"></i> Export
                                </div>
                                <ul tabIndex={0} className="dropdown-content z-[99] menu p-2 shadow-sm bg-base-100 border border-base-200 rounded-box w-52 mt-2">
                                    <li><button type="button" onClick={() => exportToCSV(dt, 'Corporate Receipts')}><i className="pi pi-file-excel text-success"></i> Download CSV</button></li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <DataTable
                        paginator
                        rows={10}
                        rowsPerPageOptions={[5, 10, 25, 50, 100]}
                        ref={dt}
                        value={receipts}
                        loading={loading}
                        globalFilter={globalFilter}
                        emptyMessage="No corporate receipts found"
                        paginatorTemplate="CurrentPageReport RowsPerPageDropdown PrevPageLink PageLinks NextPageLink"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    >
                        <Column field="receiptNumber" header="Receipt Number" sortable/>
                        <Column field="cooperateName" header="Corporate Name" sortable/>
                        <Column field="modeOfPayment" header="Payment Mode" body={paymentModeBodyTemplate} sortable/>
                        <Column field="agentName" header="Agent Name" sortable/>
                        <Column field="amount" header="Amount" body={amountBodyTemplate} sortable/>
                        <Column field="status" header="Status" body={statusBodyTemplate} sortable/>
                        <Column field="createdAt" header="Created At"
                                body={(rowData) => dateBodyTemplate(rowData, 'createdAt')} sortable/>
                        <Column field="revokedAt" header="Revoked At"
                                body={(rowData) => dateBodyTemplate(rowData, 'revokedAt')} sortable/>
                        <Column header="Actions" body={actionBodyTemplate} exportable={false}
                                style={{textAlign: 'center'}}/>
                    </DataTable>
                </AppCard>

                <ChangeStatusCooperateReceiptDialog
                    visible={showStatusDialog}
                    receipt={selectedReceipt}
                    onHide={closeStatusDialog}
                    onStatusUpdate={handleStatusUpdate}
                />
            </div>
        );
}

export default AllCooperateReceiptsTable;